/*
 * Shipping protection is checked by default. Unchecking removes the variant.
 * It is added once, kept at quantity 1, and removed when the cart has no other items.
 */
if (!customElements.get('ccl-shipping-protection')) {
  const OPT_OUT = 'ccl-ship-protect-opt-out';

  customElements.define(
    'ccl-shipping-protection',
    class CclShippingProtection extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;
        this.dataset.bound = 'true';
        this.checkbox = this.querySelector('.ccl-protect__check');
        this.checkbox?.addEventListener('change', (event) => {
          event.stopPropagation();
          this.onToggle();
        });
        this.sync();
      }

      sync() {
        if (window.__cclProtectSync) return window.__cclProtectSync;
        window.__cclProtectSync = this.runSync().finally(() => {
          window.__cclProtectSync = null;
        });
        return window.__cclProtectSync;
      }

      async runSync() {
        const variantId = this.dataset.variantId;
        if (!variantId) return;
        const other = Number(this.dataset.otherCount || 0);
        const inCart = this.dataset.inCart === 'true';
        const qty = Number(this.dataset.quantity || 0);
        const optedOut = sessionStorage.getItem(OPT_OUT) === '1';

        if (other <= 0) {
          sessionStorage.removeItem(OPT_OUT);
          if (inCart) await this.mutate('change', variantId, 0);
          return;
        }
        if (inCart && qty > 1) {
          await this.mutate('change', variantId, 1);
          return;
        }
        if (inCart || optedOut) return;
        if (this.checkbox) this.checkbox.checked = true;
        const added = await this.mutate('add', variantId, 1);
        if (!added && this.checkbox) this.checkbox.checked = false;
      }

      async onToggle() {
        if (window.__cclProtectSync) await window.__cclProtectSync;
        const variantId = this.dataset.variantId;
        if (!variantId || !this.checkbox) return;
        if (this.checkbox.checked) {
          sessionStorage.removeItem(OPT_OUT);
          if (this.dataset.inCart !== 'true') await this.mutate('add', variantId, 1);
          return;
        }
        sessionStorage.setItem(OPT_OUT, '1');
        if (this.dataset.inCart === 'true') await this.mutate('change', variantId, 0);
      }

      sectionIds() {
        const ids = [];
        if (document.querySelector('cart-drawer')) ids.push('cart-drawer', 'cart-icon-bubble');
        if (document.getElementById('main-cart-items')) ids.push('cart-items');
        if (document.getElementById('main-cart-footer')) ids.push('cart-footer');
        return ids;
      }

      async mutate(action, variantId, quantity) {
        const ids = this.sectionIds();
        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', String(quantity));
        if (ids.length) {
          body.append('sections', ids.join(','));
          body.append('sections_url', window.location.pathname);
        }
        const url = action === 'add' ? routes.cart_add_url : routes.cart_change_url;
        const response = await fetch(url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (!response.ok || data.status) return false;
        let sections = data.sections;
        if (!sections) {
          const ids = this.sectionIds();
          if (ids.length) {
            const fresh = await fetch(`${routes.cart_url}?sections=${encodeURIComponent(ids.join(','))}`);
            if (fresh.ok) sections = await fresh.json();
          }
        }
        this.applySections(sections || {});
        return true;
      }

      applySections(sections) {
        if (sections['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          const freshRoot = parsed.querySelector('cart-drawer');
          const currentRoot = document.querySelector('cart-drawer');
          const freshInner = parsed.querySelector('#CartDrawer');
          const currentInner = document.querySelector('#CartDrawer');
          if (freshRoot && currentRoot) {
            currentRoot.classList.toggle('is-empty', freshRoot.classList.contains('is-empty'));
          }
          if (freshInner && currentInner) currentInner.innerHTML = freshInner.innerHTML;
          const overlay = currentRoot?.querySelector('#CartDrawer-Overlay');
          if (overlay && typeof currentRoot.close === 'function') {
            overlay.addEventListener('click', currentRoot.close.bind(currentRoot));
          }
        }

        if (sections['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const source = parsed.querySelector('.shopify-section');
          const bubble = document.getElementById('cart-icon-bubble');
          if (source && bubble) bubble.innerHTML = source.innerHTML;
        }

        if (sections['cart-items']) {
          const parsed = new DOMParser().parseFromString(sections['cart-items'], 'text/html');
          const source = parsed.querySelector('cart-items');
          const target = document.querySelector('cart-items');
          if (source && target) target.innerHTML = source.innerHTML;
        }

        if (sections['cart-footer']) {
          const parsed = new DOMParser().parseFromString(sections['cart-footer'], 'text/html');
          const source = parsed.querySelector('#main-cart-footer');
          const target = document.getElementById('main-cart-footer');
          if (source && target) target.innerHTML = source.innerHTML;
        }
      }
    }
  );
}

/*
 * Drawer checkout buttons (Oct 12 2026).
 * "Go to Protected Checkout": make sure exactly one protection item is in the cart, then go to checkout.
 * "Checkout without protection": remove the protection item if present, then go to checkout.
 * If the protection add fails, stay in the drawer and say so (never claim protection that isn't there).
 */
(() => {
  if (window.__cclProtectedCheckout) return;
  window.__cclProtectedCheckout = true;
  let busy = false;

  const post = async (url, payload) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    return response.ok;
  };

  const currentQty = async (variantId) => {
    const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
    return (cart.items || [])
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((sum, item) => sum + item.quantity, 0);
  };

  const ONLY_MSG = 'Shipping Protection is an add-on. Please add a product to your cart first.';
  const PROTECT_HANDLE = 'shipping-protection';
  const isProtect = (item, variantId) =>
    String(item.variant_id) === String(variantId) || item.handle === PROTECT_HANDLE;
  const cartCounts = async (variantId) => {
    const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
    let protect = 0;
    let other = 0;
    (cart.items || []).forEach((item) => {
      if (isProtect(item, variantId)) protect += item.quantity;
      else other += item.quantity;
    });
    return { protect, other, cart };
  };
  window.__cclCartCounts = cartCounts;

  const showError = (button, text) => {
    const box = button.closest('.cart__ctas')?.querySelector('.ccl-checkout__error');
    if (box) {
      box.textContent = text;
      box.hidden = false;
    }
  };

  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest && event.target.closest('[data-ccl-checkout]');
      if (!button || button.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      if (busy) return;
      busy = true;
      button.setAttribute('aria-busy', 'true');
      const variantId = button.dataset.variantId;
      const mode = button.dataset.cclCheckout;
      try {
        const counts = await cartCounts(variantId);
        const qty = counts.protect;
        if (counts.other === 0) {
          // Protection is an add-on only: never check out a protection-only cart.
          if (qty > 0) await post('/cart/change.js', { id: String(variantId), quantity: 0 });
          showError(button, ONLY_MSG);
          return;
        }
        if (mode === 'protected') {
          let ok = true;
          if (qty === 0) ok = await post('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
          else if (qty > 1) ok = await post('/cart/change.js', { id: String(variantId), quantity: 1 });
          if (!ok) {
            showError(button, 'Shipping Protection could not be added. Use "Checkout without protection" or try again.');
            return;
          }
        } else if (qty > 0) {
          await post('/cart/change.js', { id: String(variantId), quantity: 0 });
          sessionStorage.setItem('ccl-ship-protect-opt-out', '1');
        }
        window.location.href = '/checkout';
      } catch (error) {
        showError(button, 'Something went wrong. Please try again.');
      } finally {
        busy = false;
        button.removeAttribute('aria-busy');
      }
    },
    true
  );
})();

/*
 * Protection-only guard for every other checkout entry (cart page Checkout button,
 * /checkout links, dynamic checkout). If the cart holds only Shipping Protection,
 * remove it and show a message instead of going to checkout (Oct 13 2026).
 */
(() => {
  if (window.__cclProtectOnlyGuard) return;
  window.__cclProtectOnlyGuard = true;
  const MSG = 'Shipping Protection is an add-on. Please add a product to your cart first.';
  let passed = false;
  const counts = async () => {
    const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
    let protect = 0, other = 0, variant = null;
    (cart.items || []).forEach((item) => {
      if (item.handle === 'shipping-protection') { protect += item.quantity; variant = item.key; }
      else other += item.quantity;
    });
    return { protect, other, variant };
  };
  const block = async (event, proceed) => {
    if (passed) return;
    event.preventDefault();
    event.stopPropagation();
    const c = await counts().catch(() => null);
    if (c && c.other === 0 && c.protect > 0) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: c.variant, quantity: 0 }),
      });
      window.alert(MSG);
      window.location.reload();
      return;
    }
    passed = true;
    proceed();
  };
  document.addEventListener(
    'click',
    (event) => {
      const t = event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"], a[href^="/checkout"]');
      if (!t || t.closest('[data-ccl-checkout]')) return;
      block(event, () => {
        if (t.tagName === 'A') window.location.href = t.href;
        else window.location.href = '/checkout';
      });
    },
    true
  );
})();
